import React, { useEffect, useMemo, useState } from 'react'
import { formatDateTime, formatCurrency, parseCurrency } from '@/utils/dateFormat'
import {
  getOffers,
  createOffer,
  getAssessment,
  OfferInput,
} from '@/api/tapResources'
import { TapOffer, TapAssessment } from '@/types/index'
import { Modal } from '@/components/ui/Modal'
import { AlertCircle, Plus } from 'lucide-react'

// Feature #158 — Offer tab.
// Shows offer history (most recent first, each offer is effectively a
// negotiation revision), surfaces a warning if the latest offer's salary or
// employment type diverges from the approved assessment, and lets the user
// capture a verbal offer or record a new revision.
interface OfferTabProps {
  applicationId: string
}

const EMPLOYMENT_TYPES = ['Permanent Full Time', 'Permanent Part Time', 'Fixed Term', 'Casual', 'Contract']
const VERBAL_RESULTS = ['Accepted', 'Declined', 'Negotiating', 'Pending']

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

const OfferTab: React.FC<OfferTabProps> = ({ applicationId }) => {
  const [offers, setOffers] = useState<TapOffer[]>([])
  const [assessment, setAssessment] = useState<TapAssessment | null>(null)
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const [salary, setSalary] = useState('')
  const [salaryUnit, setSalaryUnit] = useState('Annual')
  const [employmentType, setEmploymentType] = useState('')
  const [location, setLocation] = useState('')
  const [verbalOfferResult, setVerbalOfferResult] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const [offersResult, assessmentResult] = await Promise.all([
        getOffers(applicationId),
        getAssessment(applicationId),
      ])
      // Most recent first.
      setOffers([...offersResult].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ))
      setAssessment(assessmentResult)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  // Requirements §3.9.2: warn when an offer diverges from approved
  // determinations — it implies the offer needs to go back for re-approval.
  const latestOffer = offers[0]
  const reapprovalWarning = useMemo(() => {
    if (!latestOffer || !assessment || assessment.approvalStatus !== 'Approved') return null
    const reasons: string[] = []
    if (latestOffer.salary != null && assessment.determinedSalaryMax != null &&
        latestOffer.salary > assessment.determinedSalaryMax) {
      reasons.push(`Salary (${formatCurrency(latestOffer.salary)}) exceeds approved max (${formatCurrency(assessment.determinedSalaryMax)}).`)
    }
    if (latestOffer.salary != null && assessment.determinedSalaryMin != null &&
        latestOffer.salary < assessment.determinedSalaryMin) {
      reasons.push(`Salary (${formatCurrency(latestOffer.salary)}) is below approved min (${formatCurrency(assessment.determinedSalaryMin)}).`)
    }
    if (latestOffer.employmentType && assessment.determinedEmploymentType &&
        latestOffer.employmentType !== assessment.determinedEmploymentType) {
      reasons.push(`Employment type "${latestOffer.employmentType}" differs from approved "${assessment.determinedEmploymentType}".`)
    }
    return reasons.length ? reasons : null
  }, [latestOffer, assessment])

  const openCreate = () => {
    // Pre-populate with the latest offer's values so a new revision is quick
    // to capture — most negotiations only tweak one field.
    setSalary(latestOffer?.salary?.toString() || assessment?.determinedSalaryMax?.toString() || '')
    setSalaryUnit(latestOffer?.salaryUnit || assessment?.salaryUnit || 'Annual')
    setEmploymentType(latestOffer?.employmentType || assessment?.determinedEmploymentType || '')
    setLocation(latestOffer?.location || '')
    setVerbalOfferResult('')
    setModalOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload: OfferInput = {
        salary: salary ? parseFloat(salary) : undefined,
        salaryUnit: salaryUnit || undefined,
        employmentType: employmentType || undefined,
        location: location || undefined,
        verbalOfferResult: verbalOfferResult || undefined,
      }
      await createOffer(applicationId, payload)
      setModalOpen(false)
      await load()
    } catch {
      alert('Failed to save offer')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Offer</h3>
        <button onClick={openCreate} style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={14} /> {offers.length ? 'New revision' : 'Record offer'}
        </button>
      </div>

      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}

      {!loading && reapprovalWarning && (
        <div style={{
          ...cardStyle,
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
        }}>
          <AlertCircle size={18} style={{ color: '#b45309', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', marginBottom: '4px' }}>
              Re-approval required
            </div>
            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '13px', color: '#78350f' }}>
              {reapprovalWarning.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        </div>
      )}

      {!loading && offers.length === 0 && (
        <div style={cardStyle}>
          <div style={{ color: 'var(--color-dark-grey)' }}>No offers recorded yet.</div>
        </div>
      )}

      {offers.map((o, idx) => (
        <div key={o.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600 }}>
                {idx === 0 ? 'Current offer' : `Revision ${offers.length - idx}`}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                {formatDateTime(o.createdAt)}
              </div>
            </div>
            {o.verbalOfferResult && (
              <span style={{
                padding: '4px 10px',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: 'var(--color-light-grey)',
              }}>
                {o.verbalOfferResult}
              </span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
            <div>
              {/* Bug #174: render salary as currency ($xxx,xxx) */}
              <div style={labelStyle}>Salary</div>
              <div style={{ fontSize: '14px' }}>
                {o.salary != null ? `${formatCurrency(o.salary)} (${o.salaryUnit || '—'})` : '—'}
              </div>
            </div>
            <div>
              <div style={labelStyle}>Employment</div>
              <div style={{ fontSize: '14px' }}>{o.employmentType || '—'}</div>
            </div>
            <div>
              <div style={labelStyle}>Location</div>
              <div style={{ fontSize: '14px' }}>{o.location || '—'}</div>
            </div>
          </div>
        </div>
      ))}

      <Modal
        isOpen={modalOpen}
        title={offers.length ? 'New offer revision' : 'Record offer'}
        onClose={() => setModalOpen(false)}
        isDirty={salary !== '' || location !== '' || verbalOfferResult !== ''}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setModalOpen(false)} style={secondaryBtn}>Cancel</button>
            <button onClick={handleSave} style={primaryBtn} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              {/* Bug #174: text input so pasted "$75,000" values are accepted;
                  parseCurrency strips non-numeric chars on blur/save. */}
              <label style={labelStyle}>Salary</label>
              <input
                type="text"
                inputMode="decimal"
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData('text')
                  const num = parseCurrency(pasted)
                  if (!isNaN(num)) {
                    e.preventDefault()
                    setSalary(String(num))
                  }
                }}
                placeholder="e.g. $75,000"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Unit</label>
              <select
                value={salaryUnit}
                onChange={(e) => setSalaryUnit(e.target.value)}
                style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
              >
                <option value="Annual">Annual</option>
                <option value="Hourly">Hourly</option>
                <option value="Daily">Daily</option>
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Employment Type</label>
            <select
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value)}
              style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
            >
              <option value="">—</option>
              {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Location</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Verbal offer result</label>
            <select
              value={verbalOfferResult}
              onChange={(e) => setVerbalOfferResult(e.target.value)}
              style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
            >
              <option value="">—</option>
              {VERBAL_RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default OfferTab
